import { useMemo, type ReactNode } from 'react'
import { VideoTrack } from '@livekit/components-react'
import { Track } from 'livekit-client'
import { useEventLiveKitTrackRefs } from '@/components/livekit/EventLiveKitProvider'
import { sourceTrack } from '@/lib/watch/rules'

/**
 * A source's live video, bare, for the caller's own frame (spheres, strip tiles, faces). `identity` is a source id
 * (`WatchCamera.identity`): a host's screen share (`<identity>:screen`) plays their screen-share track; anything else
 * plays the participant's camera first and any other video after it but a screen share, as `StreamVideoSurface` does,
 * so a host's face never turns into their screen. A muted track shows the fallback, matching the shared liveness rule,
 * so a paused camera never leaves a frozen frame.
 */
export function LiveTrack({ identity: source, className, fallback = null }: { identity: string; className?: string; fallback?: ReactNode }) {
  const refs = useEventLiveKitTrackRefs()
  const trackRef = useMemo(() => {
    const { identity, screen } = sourceTrack(source)
    const playing = refs.filter((ref) => ref.participant.identity === identity && !ref.publication.isMuted)
    if (screen) return playing.find((ref) => ref.source === Track.Source.ScreenShare) ?? null
    return playing.find((ref) => ref.source === Track.Source.Camera) ?? playing.find((ref) => ref.source !== Track.Source.ScreenShare) ?? null
  }, [refs, source])

  if (!trackRef) return <>{fallback}</>
  return (
    <VideoTrack
      trackRef={trackRef}
      playsInline
      muted
      controls={false}
      disablePictureInPicture
      disableRemotePlayback
      className={className}
    />
  )
}
